'use client';

/**
 * CONTENT QUEUE ONE-FOCUS DECK — posts are the cards (/api/content/posts).
 * Focus: WHY = hook + platform + schedule, PREVIEW = full caption + creative
 * (image/video path render), DECIDE = ▶ PUBLISH / ⟳ RETRY (real
 * POST /api/content/publish) + deep-link to the classic station.
 * ⚡ AUTO publishes every approved post sequentially.
 */
import { useCallback, useEffect, useState } from 'react';
import { OneFocusDeck, bigVerb, CY, stColor, type OFItem, type OFAdapter } from './OneFocus';
import { mono, display } from '@/future/primitives';

const PLAT: Record<string, string> = { linkedin: 'in', instagram: '◎', youtube: '▶', tiktok: '♪', x: '𝕏' };

function Why({ item }: { item: OFItem }) {
  const p = item.raw;
  return (
    <>
      {[['PLATFORM', p.platform?.toUpperCase()], ['STATUS', p.status?.toUpperCase()],
        ['SCHEDULED', p.scheduled_at ? new Date(p.scheduled_at).toLocaleString() : '—'],
        ['POSTED', p.posted_at ? new Date(p.posted_at).toLocaleString() : '—'],
        ['TYPE', (p.content_type || 'image').toUpperCase()]].map(([k, v]) => (
        <div key={k as string} className="flex justify-between" style={{ padding: '4px 0' }}>
          <span style={{ ...mono(15, '.16em'), color: '#7fa9bd' }}>{k}</span>
          <span style={{ ...mono(16, '.06em'), color: '#d7f2ff' }}>{v || '—'}</span>
        </div>
      ))}
      <div style={{ marginTop: 14, borderTop: '1px solid rgba(125,232,255,.12)', paddingTop: 10 }}>
        <div style={{ ...mono(14, '.26em'), color: '#49677a', marginBottom: 6 }}>HOOK</div>
        <div style={{ ...display(26, 300), color: '#e9f9ff', lineHeight: 1.5 }}>{p.hook || '(no hook)'}</div>
      </div>
      {p.error && (
        <div style={{ marginTop: 12, padding: '9px 12px', borderRadius: 10, borderLeft: '2px solid #ff7d8a', background: 'rgba(255,125,138,.06)' }}>
          <div style={{ ...mono(13, '.2em'), color: '#ff7d8a', marginBottom: 4 }}>LAST ERROR</div>
          <div style={{ ...display(21, 300), color: '#9fb8c6' }}>{p.error}</div>
        </div>
      )}
    </>
  );
}

function Preview({ item }: { item: OFItem }) {
  const p = item.raw;
  const media = p.image_path || p.video_path;
  const isVideo = !!p.video_path && !p.image_path;
  return (
    <>
      {media && (
        isVideo
          ? <video src={media} controls style={{ width: '100%', maxHeight: 300, borderRadius: 10, background: '#000', marginBottom: 12 }} />
          : /* eslint-disable-next-line @next/next/no-img-element */
            <img src={media} alt="creative" style={{ width: '100%', maxHeight: 300, objectFit: 'contain', borderRadius: 10, background: '#000', marginBottom: 12 }} />
      )}
      <div style={{ ...mono(14, '.26em'), color: '#49677a', marginBottom: 8 }}>CAPTION · AS IT WILL POST</div>
      <div style={{ ...display(25, 300), color: 'var(--uif-t-body)', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{p.caption || '(no caption)'}</div>
      {p.post_url && <a href={p.post_url} target="_blank" rel="noreferrer" style={{ ...mono(16, '.16em'), color: CY, display: 'block', marginTop: 12, textDecoration: 'none' }}>LIVE POST ↗</a>}
    </>
  );
}

function publishPost(id: string) {
  return fetch('/api/content/publish', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ postId: id }) });
}

function Decide({ item, refresh, advance, speak }: { item: OFItem; refresh: () => void; advance: () => void; speak: (t: string) => void }) {
  const p = item.raw;
  const [busy, setBusy] = useState(false);
  const publishable = ['approved', 'failed', 'draft', 'pending_review'].includes(p.status);
  const act = async () => {
    if (busy) return;
    setBusy(true);
    try { await publishPost(p.id); refresh(); speak(`Published — the ${p.platform} post is on its way.`); advance(); } finally { setBusy(false); }
  };
  return (
    <>
      {publishable ? (
        <div onMouseUp={act} style={{ ...bigVerb(), opacity: busy ? .6 : 1, textAlign: 'center' }}>
          {busy ? 'PUBLISHING…' : p.status === 'failed' ? '⟳ RETRY PUBLISH' : '▶ PUBLISH'}
        </div>
      ) : (
        <div style={{ padding: '14px 0', textAlign: 'center', borderRadius: 12, border: `1px solid color-mix(in srgb, ${stColor(p.status)} 40%, transparent)`, color: stColor(p.status), ...mono(18, '.26em') }}>
          {p.status?.toUpperCase()}{p.posted_at ? ` · ${new Date(p.posted_at).toLocaleDateString()}` : ''}
        </div>
      )}
      <a href={`/content/${p.id}`} style={{ textDecoration: 'none' }}>
        <div style={{ width: '100%', padding: '10px 0', marginTop: 10, borderRadius: 12, background: 'transparent', border: '1px solid rgba(125,232,255,.3)', color: CY, ...mono(16, '.26em'), textAlign: 'center', cursor: 'pointer' }}>
          OPEN IN STUDIO ↗
        </div>
      </a>
      <div style={{ ...mono(13, '.12em'), color: '#3d4654', marginTop: 10, textAlign: 'center' }}>POST /api/content/publish · REAL PIPE</div>
    </>
  );
}

export function ContentDeckSurface() {
  const [items, setItems] = useState<OFItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const load = useCallback(() => {
    fetch('/api/content/posts').then(r => r.json()).then(d => {
      const posts: any[] = Array.isArray(d) ? d : d.posts || [];
      const rank = (s: string) => ['approved', 'pending_review', 'draft', 'failed', 'posted'].indexOf(s) + 1 || 9;
      posts.sort((a, b) => rank(a.status) - rank(b.status));
      setItems(posts.map((p: any): OFItem => ({
        id: p.id, biz: p.project_id, title: p.hook ? String(p.hook).slice(0, 60) : '(untitled)',
        type: `${PLAT[p.platform] || ''} ${(p.platform || '').toUpperCase()}`.trim(),
        status: p.status,
        kpis: [
          { l: 'TYPE', v: (p.content_type || 'image').toUpperCase() },
          { l: 'SCHEDULED', v: p.scheduled_at ? new Date(p.scheduled_at).toLocaleDateString() : '—' },
        ],
        taste: p.caption ? String(p.caption).slice(0, 200) : undefined,
        foot: p.post_url || p.id, raw: p,
      })));
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 60_000); return () => clearInterval(t); }, [load]);

  const adapter: OFAdapter = {
    kind: 'content',
    strap: (its) => `ONE FOCUS · ${its.length} POSTS · PUBLISH IS THE DECISION`,
    decidable: (it) => ['approved'].includes(it.status || ''),
    Why, Preview, Decide,
    auto: { label: (n) => `PUBLISH APPROVED${n ? ` (${n})` : ''}`, run: async (it) => { await publishPost(it.raw.id); } },
  };
  return <OneFocusDeck items={items} loaded={loaded} adapter={adapter} refresh={load} />;
}
